import ChapterBadge from "./ChapterBadge";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  CaretDown,
  Factory,
  Bank,
  FirstAidKit,
  ShoppingCart,
  Buildings,
  Truck,
  Briefcase,
  GraduationCap,
  Bank as PublicSectorIcon,
  Lightning
} from "@phosphor-icons/react";

const IndustryEcosystem = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [expandedIndustry, setExpandedIndustry] = useState("Manufacturing");

  const crossIndustryPlatforms = [
    "Tally", "Zoho", "Salesforce", "SAP", "Oracle", "Microsoft Dynamics 365",
    "Freshworks", "Darwinbox", "LeadSquared", "Razorpay", "ServiceNow", 
    "NetSuite", "Workday", "HubSpot", "Jira / Atlassian"
  ];

  const industries = [
    {
      id: "Manufacturing",
      icon: Factory,
      name: "Manufacturing",
      platforms: ["SAP", "Oracle", "Microsoft Dynamics 365", "Infor", "Salesforce", "Tally", "Zoho", "Freshworks", "ServiceNow", "Siemens", "Blue Yonder", "Manhattan"],
      tags: ["Finance & ERP", "CRM & Sales", "Supply Chain", "Manufacturing Operations", "Procurement", "Service", "HR & Payroll", "Analytics"]
    },
    {
      id: "Financial Services",
      icon: Bank,
      name: "Financial Services",
      platforms: ["Salesforce", "Oracle", "SAP", "Microsoft Dynamics 365", "Tally", "Zoho", "Darwinbox", "LeadSquared", "Razorpay", "Freshworks", "ServiceNow", "Temenos", "Finastra", "nCino"],
      tags: ["Finance", "CRM", "Lending & Collections", "Payments", "Service", "HR & Payroll", "Risk & Analytics", "Workflow"]
    },
    {
      id: "Healthcare Providers",
      icon: FirstAidKit,
      name: "Healthcare Providers",
      platforms: ["Salesforce", "Oracle Health", "Microsoft Dynamics 365", "SAP", "Tally", "Zoho", "Freshworks", "Darwinbox", "Razorpay", "ServiceNow", "Epic", "athenahealth"],
      tags: ["Clinical Systems", "Finance", "CRM & Patient Engagement", "Scheduling & Operations", "HR & Payroll", "Service", "Payments", "Analytics"]
    },
    {
      id: "Retail / Consumer",
      icon: ShoppingCart,
      name: "Retail / Consumer",
      platforms: ["Shopify", "Salesforce", "SAP", "Oracle Retail", "Microsoft Dynamics 365", "Tally", "Zoho", "Freshworks", "Razorpay", "ServiceNow", "Manhattan", "Blue Yonder", "HubSpot"],
      tags: ["Commerce & POS", "Inventory & Fulfillment", "CRM & Marketing", "Finance", "Payments", "Support", "HR & Payroll", "Analytics"]
    },
    {
      id: "Construction / Real Estate",
      icon: Buildings,
      name: "Construction / Real Estate",
      platforms: ["Procore", "Autodesk Construction Cloud", "Oracle Primavera", "Aconex", "Salesforce", "SAP", "Oracle", "Microsoft Dynamics 365", "Tally", "Zoho", "Freshworks", "Darwinbox", "ServiceNow"],
      tags: ["Project Controls", "Finance & ERP", "CRM & Sales", "Procurement", "Site Workflow", "HR & Payroll", "Support", "Analytics"]
    },
    {
      id: "Transportation / Logistics",
      icon: Truck,
      name: "Transportation / Logistics",
      platforms: ["Oracle Transportation Management", "SAP", "Microsoft Dynamics 365", "Manhattan", "Blue Yonder", "Salesforce", "Tally", "Zoho", "Freshworks", "Razorpay", "ServiceNow", "project44", "FourKites"],
      tags: ["Transport", "Warehousing", "Finance", "CRM", "Service", "Payments", "HR & Payroll", "Analytics"]
    },
    {
      id: "Professional Services",
      icon: Briefcase,
      name: "Professional Services",
      platforms: ["Salesforce", "Microsoft Dynamics 365", "NetSuite", "Workday", "Jira", "Zoho", "Freshworks", "Darwinbox", "LeadSquared", "Razorpay", "ServiceNow", "HubSpot"],
      tags: ["Sales & Pipeline", "Projects", "Billing & Finance", "HR & Talent", "Service", "Payments", "Collaboration", "Analytics"]
    },
    {
      id: "Education",
      icon: GraduationCap,
      name: "Education",
      platforms: ["Salesforce Education Cloud", "Microsoft Dynamics 365", "Oracle", "SAP", "Zoho", "Freshworks", "Darwinbox", "Razorpay", "ServiceNow", "Canvas", "Blackboard", "Ellucian"],
      tags: ["Admissions & CRM", "Student Systems", "Finance", "HR & Payroll", "Payments", "Support", "Operations", "Analytics"]
    },
    {
      id: "Public Sector",
      icon: PublicSectorIcon,
      name: "Public Sector",
      platforms: ["Oracle", "SAP", "Salesforce", "Microsoft Dynamics 365", "ServiceNow", "Tally", "Zoho", "Freshworks", "Darwinbox", "Razorpay"],
      tags: ["Citizen Service", "Finance & Procurement", "HR & Payroll", "Workflow", "Support", "Payments", "Project Tracking", "Analytics"]
    },
    {
      id: "Energy / Utilities / Telecom",
      icon: Lightning,
      name: "Energy / Utilities / Telecom",
      platforms: ["Oracle Utilities", "Salesforce Industries", "SAP", "Microsoft Dynamics 365", "ServiceNow", "Tally", "Zoho", "Freshworks", "Darwinbox", "Razorpay"],
      tags: ["Billing & Revenue", "CRM & Service", "Field Operations", "Asset / Work Management", "Finance", "HR & Payroll", "Payments", "Analytics"]
    }
  ];

  const toggleIndustry = (industryId) => {
    setExpandedIndustry(expandedIndustry === industryId ? null : industryId);
  };

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative"
      data-testid="industry-ecosystem-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0B]" />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <ChapterBadge number="06" name="Ecosystem" />
          <span className="mono-label">Enterprise Ecosystem</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            Designed for the platforms{" "}
            <span className="gradient-text">enterprises already run</span>
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            OnTenacity is built for the software landscape behind finance, sales, operations, people, 
            service, and sector-specific workflows — across both global enterprise suites and 
            India-relevant business platforms.
          </p>
          <p className="mt-3 text-sm text-[#71717A]">
            From ERP and CRM to payroll, project controls, support, payments, and industry operating systems.
          </p>
        </motion.div>

        {/* Layer 1: Cross-Industry Platforms */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <h3 className="text-xs text-[#00E5FF] uppercase tracking-wider font-medium mb-4 text-center">
            Common enterprise platforms across industries
          </h3>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {crossIndustryPlatforms.map((platform, index) => (
              <span
                key={index}
                className="px-3 py-1.5 rounded-full bg-[#0F0F10] border border-white/10 text-xs text-[#FAFAFA] hover:border-[#00E5FF]/30 transition-colors"
              >
                {platform}
              </span>
            ))}
          </div>
          <p className="text-center text-xs text-[#52525B] max-w-2xl mx-auto">
            These platforms commonly anchor finance, CRM, HR, payroll, support, analytics, and workflow 
            operations across Indian and global enterprises.
          </p>
        </motion.div>

        {/* Layer 2: Industry Ecosystem Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-2"
          data-testid="industry-accordion"
        >
          {industries.map((industry) => (
            <div
              key={industry.id}
              className={`rounded-xl border transition-all duration-300 ${
                expandedIndustry === industry.id 
                  ? "border-[#00E5FF]/30 bg-[#0F0F10]" 
                  : "border-white/5 bg-[#0F0F10]/50 hover:border-white/10"
              }`}
              data-testid={`industry-card-${industry.id.toLowerCase().replace(/\s+/g, '-').replace('/', '-')}`}
            >
              {/* Industry Header */}
              <button
                onClick={() => toggleIndustry(industry.id)}
                className="w-full px-4 md:px-5 py-3 md:py-4 flex items-center justify-between gap-3"
                data-testid={`industry-toggle-${industry.id.toLowerCase().replace(/\s+/g, '-').replace('/', '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                    expandedIndustry === industry.id 
                      ? "bg-[#00E5FF]/20" 
                      : "bg-white/5"
                  }`}>
                    <industry.icon 
                      size={18} 
                      weight="duotone" 
                      className={expandedIndustry === industry.id ? "text-[#00E5FF]" : "text-[#A1A1AA]"} 
                    />
                  </div>
                  <h3 className={`text-sm md:text-base font-semibold font-['Outfit'] transition-colors ${
                    expandedIndustry === industry.id ? "text-white" : "text-[#FAFAFA]"
                  }`}>
                    {industry.name}
                  </h3>
                </div>
                <motion.div
                  animate={{ rotate: expandedIndustry === industry.id ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <CaretDown 
                    size={18} 
                    className={expandedIndustry === industry.id ? "text-[#00E5FF]" : "text-[#71717A]"} 
                  />
                </motion.div>
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {expandedIndustry === industry.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      {/* Platforms */}
                      <div className="mb-4">
                        <span className="text-xs text-[#71717A] uppercase tracking-wider mb-2 block">
                          Platforms
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {industry.platforms.map((platform, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 rounded bg-white/5 text-xs text-[#FAFAFA]"
                            >
                              {platform}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Function Tags */}
                      <div>
                        <span className="text-xs text-[#71717A] uppercase tracking-wider mb-2 block">
                          Functions
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {industry.tags.map((tag, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 rounded border border-[#00E5FF]/20 text-xs text-[#00E5FF]/80"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </motion.div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 text-center"
        >
          <p className="text-sm text-[#A1A1AA] max-w-2xl mx-auto">
            OnTenacity is designed to understand the software landscape behind finance, sales, operations, 
            service, people, and sector-specific workflows before intelligence is activated.
          </p>
          <p className="mt-2 text-xs text-[#71717A]">
            This is what makes the diagnostic practical, not generic.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default IndustryEcosystem;
